// Business entity types (two separate companies)
export const ENTITIES = ['Brokerage', 'Property Management'];

// Lead types
export const LEAD_TYPES = ['Buyer', 'Seller', 'Renter', 'Commercial', 'PM Owner', 'Investor'];

// Lead sources
export const LEAD_SOURCES = ['Referral', 'Website', 'Sign Call', 'Social Media', 'Walk-in', 'Facebook', 'Other'];

// Commercial lead sources (shown when lead_type = commercial)
export const COMMERCIAL_LEAD_SOURCES = ['CoStar', 'LoopNet', 'Crexi', 'Broker Network', 'Referral', 'Website', 'Sign Call', 'Social Media', 'Other'];

// Commercial sub-types — determines pipeline & deal_type on conversion
export const COMMERCIAL_SUB_TYPES = [
  { key: 'buyer_rep',    label: 'Buying Property',      dealType: 'commercial_sale',  color: '#3b82f6' },
  { key: 'seller_rep',   label: 'Selling Property',     dealType: 'commercial_sale',  color: '#3b82f6' },
  { key: 'tenant_rep',   label: 'Looking for Space',    dealType: 'commercial_lease', color: '#8b5cf6' },
  { key: 'landlord_rep', label: 'Leasing Out Space',    dealType: 'commercial_lease', color: '#8b5cf6' },
];

// Commercial property types
export const COMMERCIAL_PROPERTY_TYPES = [
  { key: 'office',       label: 'Office' },
  { key: 'retail',       label: 'Retail' },
  { key: 'industrial',   label: 'Industrial' },
  { key: 'warehouse',    label: 'Warehouse' },
  { key: 'flex',         label: 'Flex Space' },
  { key: 'multifamily',  label: 'Multifamily (5+)' },
  { key: 'mixed_use',    label: 'Mixed-Use' },
  { key: 'land',         label: 'Land' },
  { key: 'hospitality',  label: 'Hospitality' },
  { key: 'medical',      label: 'Medical' },
  { key: 'self_storage', label: 'Self Storage' },
];

// Commercial lease types
export const LEASE_TYPES = [
  { key: 'nnn',            label: 'NNN (Triple Net)' },
  { key: 'gross',          label: 'Gross Lease' },
  { key: 'modified_gross', label: 'Modified Gross' },
  { key: 'ground',         label: 'Ground Lease' },
];

// Lead statuses (pipeline stages)
export const LEAD_STATUSES = ['New', 'Contacted', 'Qualified', 'Under Contract', 'Closed', 'Lost'];

// Maintenance priority levels
export const MAINT_PRIORITIES = ['urgent', 'medium', 'low'];

// Maintenance statuses
export const MAINT_STATUSES = ['New', 'AI Dispatched', 'Quotes In', 'Vendor Assigned', 'In Progress', 'Completed', 'Emergency'];

// Maintenance categories
export const MAINT_CATEGORIES = ['Plumbing', 'HVAC', 'Electrical', 'Appliance', 'General', 'Safety', 'Exterior', 'Pest Control'];

// Vendor dispatch modes
export const DISPATCH_MODES = {
  MULTI_QUOTE: 'multi-quote',      // Routine: contact multiple vendors for quotes
  FIRST_RESPONDER: 'first-responder', // Emergency: first available vendor wins
  FORMAL_BID: 'formal-bid',         // $1K+: formal bidding process
};

// Transaction milestones (NJ-specific)
// Legacy — kept for backwards compatibility
export const DEAL_MILESTONES = [
  'Proposal to Purchase',
  'Contract Signed',
  'Attorney Review',
  'Inspection',
  'Appraisal',
  'Mortgage Commitment',
  'Escrow Deposit',
  'Certificate of Occupancy',
  'Clear to Close',
  'Closing',
];

// ── Seller Rep Milestone Track ──
// Full lifecycle: from listing intake through closing
export const SELLER_MILESTONES = [
  'Listing Intake',          // Initial seller consultation
  'CMA Prepared',            // Comparative Market Analysis
  'Listing Agreement Signed',// Exclusive listing contract
  'Property Prep',           // Staging, photos, repairs
  'Listed on MLS',           // Live on MLS + Zillow/Realtor.com
  'Showings & Open Houses',  // Active marketing period
  'Offers Received',         // Reviewing buyer offers
  'Offer Accepted',          // Seller accepts an offer
  // ── Post-contract (shared with buyer) ──
  'Contract Signed',
  'Attorney Review',
  'Inspection',
  'Appraisal',
  'Mortgage Commitment',
  'Certificate of Occupancy',
  'Clear to Close',
  'Closing',
];

// ── Buyer Rep Milestone Track ──
// Full lifecycle: from needs analysis through closing
export const BUYER_MILESTONES = [
  'Needs Analysis',          // What the buyer is looking for
  'Pre-Approval',            // Mortgage pre-approval obtained
  'Property Search',         // Actively searching listings
  'Showings',                // Touring properties
  'Offer Submitted',         // Written offer sent to seller
  'Negotiation',             // Counter-offers, terms discussion
  // ── Post-contract (shared with seller) ──
  'Contract Signed',
  'Attorney Review',
  'Inspection',
  'Appraisal',
  'Mortgage Commitment',
  'Escrow Deposit',
  'Clear to Close',
  'Closing',
];

// ── Commercial Seller Rep Milestone Track ──
// Full lifecycle: valuation through closing (no MLS — uses CoStar/LoopNet)
export const COMMERCIAL_SELLER_MILESTONES = [
  'Property Valuation',       // Broker opinion of value / appraisal
  'Offering Memorandum',      // OM package with financials, photos, terms
  'Marketing Launch',         // CoStar, LoopNet, broker outreach
  'Buyer Outreach & Tours',   // Respond to inquiries, schedule tours
  'LOI Received',             // Letter of Intent from buyer
  'LOI Negotiation',          // Counter terms, price, contingencies
  'LOI Accepted',             // Agreed terms — move to contract
  // ── Post-LOI / Contract phase ──
  'Contract Signed',
  'Due Diligence Period',
  'Financing Contingency',
  'Environmental & Survey',
  'Clear to Close',
  'Closing',
];

// ── Commercial Buyer Rep Milestone Track ──
// Full lifecycle: requirements through closing
export const COMMERCIAL_BUYER_MILESTONES = [
  'Requirements Analysis',    // Location, size, use type, budget
  'Financial Pre-Qualification', // Proof of funds or lender pre-qual
  'Site Search',              // CoStar/LoopNet research, off-market outreach
  'Property Tours',           // Tour candidate properties
  'LOI Submitted',            // Letter of Intent sent to seller
  'LOI Negotiation',          // Counter terms, price, contingencies
  // ── Post-LOI / Contract phase ──
  'Contract Signed',
  'Due Diligence Period',
  'Financing Contingency',
  'Environmental & Survey',
  'Clear to Close',
  'Closing',
];

// Legacy — kept for backwards compatibility with existing data
export const COMMERCIAL_MILESTONES = [
  'LOI Signed',
  'Contract Signed',
  'Due Diligence Period',
  'Financing Contingency',
  'Clear to Close',
  'Closing',
];

// ── Commercial Tenant Rep Milestone Track ──
// Full lifecycle: space search through occupancy
export const COMMERCIAL_TENANT_REP_MILESTONES = [
  'Requirements Analysis',     // Space type, size, budget, location
  'Financial Pre-Qualification', // Proof of funds, credit check
  'Site Search',               // CoStar/LoopNet, broker outreach
  'Property Tours',            // Tour candidate spaces
  'LOI Submitted',             // Letter of Intent sent to landlord
  'LOI Negotiation',           // Counter terms, rate, TI, free rent
  'LOI Accepted',              // Terms agreed — move to lease
  // ── Post-LOI / Lease phase ──
  'Due Diligence Period',      // Environmental, zoning, ADA
  'Lease Draft',               // Attorney drafts lease
  'Lease Review',              // Both sides review and redline
  'Lease Executed',            // Signed lease
  'Build-Out',                 // Tenant improvements / fit-out
  'Occupancy',                 // Move-in / rent commencement
];

// ── Commercial Landlord Rep Milestone Track ──
// Full lifecycle: marketing space through occupancy
export const COMMERCIAL_LANDLORD_REP_MILESTONES = [
  'Space Analysis',            // Assess available space, improvements needed
  'Marketing Package',         // Brochure, floor plans, photos
  'Marketing Launch',          // CoStar, LoopNet, broker outreach
  'Tenant Outreach & Tours',   // Respond to inquiries, schedule tours
  'LOI Received',              // Letter of Intent from tenant
  'LOI Negotiation',           // Counter terms, rate, TI, free rent
  'LOI Accepted',              // Terms agreed — move to lease
  // ── Post-LOI / Lease phase ──
  'Tenant Credit Check',       // Financial qualification of tenant
  'Lease Draft',               // Attorney drafts lease
  'Lease Review',              // Both sides review and redline
  'Lease Executed',            // Signed lease
  'Build-Out',                 // Tenant improvements / fit-out
  'Occupancy',                 // Move-in / rent commencement
];

// ── Commercial CRM Pipeline (pre-contract stages for Kanban) ──
// Used in LeadsView when Commercial board is active
export const COMMERCIAL_CRM_PIPELINE = [
  { key: 'new',              label: 'New',              color: '#6366F1' },
  { key: 'contacted',        label: 'Contacted',        color: '#F59E0B' },
  { key: 'qualified',        label: 'Qualified',        color: '#0EA5E9' },
  { key: 'site_search',      label: 'Site Search',      color: '#2563EB' },
  { key: 'loi_submitted',    label: 'LOI Submitted',    color: '#8B5CF6' },
  { key: 'loi_negotiation',  label: 'LOI Negotiation',  color: '#7C3AED' },
  { key: 'loi_accepted',     label: 'LOI Accepted',     color: '#16A34A' },
];

// ── Commercial Document Checklists ──
// Template keys for commercial deal document requirements

export const COMMERCIAL_LEASE_DOCS_CHECKLIST = [
  // Pre-execution
  { kind: 'loi',                        label: 'Letter of Intent (LOI)',           required: true },
  { kind: 'tenant_financials',          label: 'Tenant Financial Statement',       required: true },
  { kind: 'environmental_phase1',       label: 'Environmental Phase I',            required: false },
  { kind: 'zoning_letter',             label: 'Zoning Verification Letter',       required: false },
  { kind: 'alta_survey',              label: 'ALTA Survey',                      required: false },
  { kind: 'ada_compliance',           label: 'ADA Compliance Report',            required: false },
  // Execution & close
  { kind: 'commercial_lease',          label: 'Commercial Lease Agreement',       required: true },
  { kind: 'estoppel_certificate',      label: 'Tenant Estoppel Certificate',     required: false },
  { kind: 'snda',                      label: 'SNDA Agreement',                  required: false },
  { kind: 'certificate_of_insurance',  label: 'Certificate of Insurance',        required: true },
  { kind: 'ti_construction_agreement', label: 'TI Construction Agreement',       required: false },
  { kind: 'commencement_date_agreement', label: 'Commencement Date Agreement',   required: true },
];

export const COMMERCIAL_SALE_DOCS_CHECKLIST = [
  // Pre-execution
  { kind: 'loi',                     label: 'Letter of Intent (LOI)',           required: true },
  { kind: 'offering_memorandum',     label: 'Offering Memorandum',             required: false },
  { kind: 'tenant_financials',       label: 'Buyer Financial Statement',       required: true },
  { kind: 'environmental_phase1',    label: 'Environmental Phase I',           required: true },
  { kind: 'environmental_phase2',    label: 'Environmental Phase II',          required: false },
  { kind: 'zoning_letter',          label: 'Zoning Verification Letter',      required: true },
  { kind: 'alta_survey',           label: 'ALTA Survey',                     required: true },
  // Execution & close
  { kind: 'psa',                     label: 'Purchase & Sale Agreement',       required: true },
  { kind: 'estoppel_certificate',    label: 'Tenant Estoppel Certificates',    required: false },
  { kind: 'snda',                    label: 'SNDA Agreements',                 required: false },
  { kind: 'rent_roll',              label: 'Rent Roll',                       required: false },
  { kind: 'certificate_of_insurance', label: 'Certificate of Insurance',      required: true },
];

// Vendor preference levels
export const VENDOR_PREFS = ['required', 'preferred', 'approved', 'blocked'];

// Team members
export const TEAM_MEMBERS = [
  'Jenifer Raffa',
  'Sylvia Micarelli',
  'Johanna Micarelli',
  'Desiree Hogbin',
  'Patricia Furrow',
  'Neha',
];

// Financial reconciliation accounts
export const RECON_ACCOUNTS = ['Trust Account', 'Operating Account', 'PM Security Deposits'];
